
import { Module } from '../types';

export const API_DATA_FETCHING_MODULE: Module = {
  id: "intermediate-api",
  title: "API Data Fetching",
  description: "Learn how to fetch data from APIs using modern React patterns",
  duration: "90 minutes",
  difficulty: "intermediate",
  content: `
# API Data Fetching in React

Learn how to fetch data from APIs using modern React patterns including useEffect, custom hooks, and error handling.

## Learning Objectives
- Understand how to fetch data in React components
- Learn to handle loading states and errors
- Create custom hooks for data fetching
- Implement proper cleanup and cancellation

## Basic Data Fetching with useEffect

\`\`\`jsx
import React, { useState, useEffect } from 'react';

function UserProfile({ userId }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchUser() {
      try {
        setLoading(true);
        const response = await fetch(\`/api/users/\${userId}\`);
        if (!response.ok) {
          throw new Error('Failed to fetch user');
        }
        const userData = await response.json();
        setUser(userData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchUser();
  }, [userId]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!user) return <div>No user found</div>;

  return (
    <div>
      <h1>{user.name}</h1>
      <p>{user.email}</p>
    </div>
  );
}
\`\`\`

## Custom Hook for Data Fetching

\`\`\`jsx
function useApi(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(url, {
          signal: controller.signal
        });
        
        if (!response.ok) {
          throw new Error(\`HTTP error! status: \${response.status}\`);
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    return () => {
      controller.abort();
    };
  }, [url]);

  return { data, loading, error };
}

// Usage
function ProductList() {
  const { data: products, loading, error } = useApi('/api/products');

  if (loading) return <div>Loading products...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      {products?.map(product => (
        <div key={product.id}>{product.name}</div>
      ))}
    </div>
  );
}
\`\`\`

## Error Handling and Retry Logic

\`\`\`jsx
function useApiWithRetry(url, maxRetries = 3) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(\`HTTP error! status: \${response.status}\`);
      }
      
      const result = await response.json();
      setData(result);
      setRetryCount(0);
    } catch (err) {
      setError(err.message);
      
      if (retryCount < maxRetries) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 1000 * Math.pow(2, retryCount)); // Exponential backoff
      }
    } finally {
      setLoading(false);
    }
  }, [url, retryCount, maxRetries]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, retry: fetchData };
}
\`\`\`

## Best Practices

1. **Always handle loading states** - Show appropriate UI during data fetching
2. **Implement error handling** - Gracefully handle network errors and API failures
3. **Use cleanup functions** - Cancel requests when components unmount
4. **Debounce search requests** - Avoid excessive API calls
5. **Cache responses when appropriate** - Use libraries like React Query for advanced caching
  `,
  codeExamples: [
    {
      title: "Basic API Call with useEffect",
      code: `useEffect(() => {
  fetch('/api/data')
    .then(response => response.json())
    .then(data => setData(data))
    .catch(error => setError(error));
}, []);`,
      explanation: "Simple API call pattern with error handling",
      language: "jsx"
    }
  ],
  practice: {
    task: "Create a custom hook called useUserData that fetches user information from an API",
    starter: `function useUserData(userId) {
  // TODO: Implement the custom hook
}

function UserComponent({ userId }) {
  // TODO: Use the hook and display user data
}`,
    solution: `function useUserData(userId) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;

    async function fetchUser() {
      try {
        setLoading(true);
        const response = await fetch(\`/api/users/\${userId}\`);
        if (!response.ok) throw new Error('Failed to fetch');
        const userData = await response.json();
        setUser(userData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchUser();
  }, [userId]);

  return { user, loading, error };
}

function UserComponent({ userId }) {
  const { user, loading, error } = useUserData(userId);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!user) return <div>No user found</div>;

  return (
    <div>
      <h2>{user.name}</h2>
      <p>{user.email}</p>
    </div>
  );
}`,
    hints: [
      "Use useState for data, loading, and error states",
      "Use useEffect to trigger the API call",
      "Handle all possible states in your component",
      "Don't forget to handle the case when userId is null"
    ],
    estimatedTime: 30
  },
  quickCheck: {
    questions: [
      {
        id: "api-q1",
        question: "What hook is commonly used for data fetching in React?",
        options: [
          "useState",
          "useEffect",
          "useContext",
          "useReducer"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "medium"
      },
      {
        id: "api-q2",
        question: "Why should you cancel API requests when a component unmounts?",
        options: [
          "To save bandwidth",
          "To prevent memory leaks and state updates on unmounted components",
          "To improve performance",
          "It's not necessary"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "medium"
      },
      {
        id: "api-q3",
        question: "What's a good practice when handling API errors?",
        options: [
          "Ignore them",
          "Always retry automatically",
          "Show user-friendly error messages",
          "Crash the application"
        ],
        correctAnswer: 2,
        correctAnswerIndex: 2,
        difficulty: "hard"
      }
    ]
  }
};
